import LZString from 'lz-string'
import type { Project } from '@/model/types'

/**
 * Anonymous share links (Track 7a). A whole design is encoded into the URL hash
 * (`#d=<payload>`), so anyone with the link can open it read-only — no backend,
 * no database. Big designs that won't fit in a URL are rejected up-front (see
 * `MAX_SHARE_LEN`) rather than producing a silently-broken link.
 */

/** Hash prefix that marks a shared design. */
export const SHARE_HASH_PREFIX = '#d='

/**
 * Max encoded payload length before we refuse to build a link. Browsers and
 * proxies cap URL length well above this; ~30k of LZ-compressed JSON covers any
 * normal home with room to spare. Cloud sharing (Drive) will handle the rest.
 */
export const MAX_SHARE_LEN = 30_000

/** Compress a project into the URL-safe payload string. */
export function encodeSharePayload(project: Project): string {
  return LZString.compressToEncodedURIComponent(JSON.stringify(project))
}

export interface ShareLink {
  /** Full shareable URL (origin + path + `#d=<payload>`). */
  url: string
  /** Encoded payload length, for the size guard. */
  length: number
  /** True when the payload exceeds `MAX_SHARE_LEN` — caller should not offer the copy. */
  tooLarge: boolean
}

/** Build a shareable URL for the current page from a project. */
export function buildShareLink(
  project: Project,
  base = `${location.origin}${location.pathname}`,
): ShareLink {
  const payload = encodeSharePayload(project)
  return {
    url: `${base}${SHARE_HASH_PREFIX}${payload}`,
    length: payload.length,
    tooLarge: payload.length > MAX_SHARE_LEN,
  }
}

/**
 * If the current location hash carries a shared design, decode it to the raw
 * (un-migrated) object. Returns null when there's no share or the payload is
 * unreadable — callers then fall back to the normal local-load path.
 * The caller is responsible for `migrateProject()` + validation.
 */
export function decodeShareFromHash(hash: string = location.hash): unknown | null {
  if (!hash.startsWith(SHARE_HASH_PREFIX)) return null
  const payload = hash.slice(SHARE_HASH_PREFIX.length)
  if (!payload) return null
  try {
    const json = LZString.decompressFromEncodedURIComponent(payload)
    if (!json) return null
    return JSON.parse(json)
  } catch {
    return null
  }
}

/** Whether the current location is showing a shared design. */
export function hasShareHash(hash: string = location.hash): boolean {
  return hash.startsWith(SHARE_HASH_PREFIX) && hash.length > SHARE_HASH_PREFIX.length
}

/** Remove the `#d=` share hash without reloading (used after Fork). */
export function clearShareHash(): void {
  if (hasShareHash()) {
    history.replaceState(null, '', location.pathname + location.search)
  }
}
